/**
 * GridJS Skeleton Loader Utilities
 * 
 * Utilities for showing/hiding Bootstrap-based skeleton loaders with GridJS tables.
 * The skeleton is rendered server-side in HTML and hidden via JavaScript when data loads.
 * 
 * For pagination support, call setupGridSkeleton() which keeps the skeleton available
 * and shows it during page changes.
 */

/**
 * Shows the skeleton loader overlay on the grid
 * @param {HTMLElement} skeletonTarget - The skeleton wrapper element
 * @param {HTMLElement} containerTarget - The GridJS container element
 */
export function showGridSkeleton(skeletonTarget, containerTarget) {
  if (skeletonTarget) {
    skeletonTarget.style.display = ''
    skeletonTarget.classList.remove('skeleton-fade-out')
    skeletonTarget.classList.remove('hidden')
  }
  if (containerTarget) {
    containerTarget.style.opacity = '0.3'
    containerTarget.style.pointerEvents = 'none'
    // Add class to hide GridJS loading bar via CSS
    containerTarget.classList.add('gridjs-loading-hidden')
  }
}

/**
 * Hides the skeleton loader with fade animation
 * @param {HTMLElement} skeletonTarget - The skeleton wrapper element
 * @param {HTMLElement} containerTarget - The GridJS container element
 * @param {boolean} removeOnHide - Whether to remove skeleton from DOM (default: false for pagination support)
 */
export function hideGridSkeleton(skeletonTarget, containerTarget, removeOnHide = false) {
  if (skeletonTarget) {
    skeletonTarget.classList.add('skeleton-fade-out')
    if (removeOnHide) {
      setTimeout(() => skeletonTarget.remove(), 200)
    } else {
      setTimeout(() => {
        skeletonTarget.style.display = 'none'
        skeletonTarget.classList.add('hidden')
        skeletonTarget.classList.remove('skeleton-fade-out')
      }, 200)
    }
  }
  if (containerTarget) {
    containerTarget.style.display = ''
    containerTarget.style.opacity = '1'
    containerTarget.style.pointerEvents = ''
    // Remove class that hides GridJS loading bar
    containerTarget.classList.remove('gridjs-loading-hidden')
  }
}

/**
 * Setup skeleton for a GridJS instance with pagination support.
 * Call this after grid.render() to enable skeleton on page changes.
 * 
 * @param {Grid} grid - The GridJS instance
 * @param {HTMLElement} skeletonTarget - The skeleton wrapper element
 * @param {HTMLElement} containerTarget - The GridJS container element
 */
export function setupGridSkeleton(grid, skeletonTarget, containerTarget) {
  // Initial hide of skeleton is handled by the `then` callback
  
  // Show skeleton when a server request starts (pagination, sorting, etc.)
  // We detect this by listening for clicks on pagination and sort headers
  const wrapper = containerTarget.closest('[data-controller]')
  
  if (wrapper) {
    // Listen for pagination clicks
    wrapper.addEventListener('click', (e) => {
      const isPaginationClick = e.target.closest('.gridjs-pagination button, .gridjs-pagination a')
      const isSortClick = e.target.closest('.gridjs-th[data-column-id]')
      
      if (isPaginationClick || isSortClick) {
        showGridSkeleton(skeletonTarget, containerTarget)
      }
    })
  }
}

/**
 * Check if skeleton is currently visible
 * @param {HTMLElement} skeletonTarget - The skeleton wrapper element
 * @returns {boolean}
 */
export function isSkeletonVisible(skeletonTarget) {
  return skeletonTarget && skeletonTarget.style.display !== 'none'
}
